import { Action, Actions } from "@/components/ai-elements/actions";
import { Shimmer } from "@/components/ai-elements/shimmer";
import {
  getHeadersWithUniqueVisitorId,
  SectionBasedProduct,
  sendMessageFeedback,
  setConversationSharing,
} from "@brandclub/common-ui";
import { useTheme } from "@mui/material";
import {
  CopyIcon,
  ShareIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
} from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";
import { ProductPreview } from "../types/conversation";
import { renderMessageContentWithCarousels } from "../utils/messageRenderer";
import { ChainOfThought } from "./ChainOfThought";

interface ChatMessageProps {
  role: "user" | "assistant";
  content: string;
  bottomRef?: React.RefObject<HTMLDivElement>;
  isTyping?: boolean;
  isStreaming?: boolean;
  isLastMessage?: boolean;
  messageIndex?: number;
  conversationId?: string;
  feedback?: { type: "like" | "dislike"; timestamp: string };
  followUpQueries?: string[];
  productMetadataMap: Map<string, SectionBasedProduct>;
  streamingProductGroups: Map<number, string[]>;
  currentStatus?: string;
  previewSearchQueries?: string[];
  previewQueryProducts?: Array<{
    query: string;
    queryIndex: number;
    products: ProductPreview[];
    totalResults: number;
  }>;
  isWaitingForFollowUpQueries?: boolean;
  featuredProduct?: {
    product: SectionBasedProduct;
    question: string;
  };
  streamingFeaturedProduct?: {
    product: SectionBasedProduct;
    question: string;
  };
  imageUrl?: string;
  imageData?: string;
  imageMimeType?: string;
  hasImage?: boolean;
  onSearch?: (query: string) => void;
  onRetry?: (messageIndex: number) => Promise<void>;
  onUpdateFeedback?: (
    messageIndex: number,
    feedback: { type: "like" | "dislike"; timestamp: string } | null
  ) => void;
}

const ChatMessageComponent: React.FC<ChatMessageProps> = React.forwardRef<
  HTMLDivElement,
  ChatMessageProps
>(
  ({
    role,
    content,
    bottomRef,
    isTyping = false,
    isStreaming = false,
    isLastMessage = false,
    messageIndex,
    conversationId,
    feedback,
    productMetadataMap,
    streamingProductGroups,
    currentStatus,
    previewSearchQueries = [],
    previewQueryProducts = [],
    featuredProduct,
    streamingFeaturedProduct,
    imageUrl,
    imageData,
    imageMimeType,
    onRetry,
    onUpdateFeedback,
  }) => {
    const theme = useTheme();
    const isUser = role === "user";
    // Initialize feedback state from props
    const [liked, setLiked] = useState(feedback?.type === "like");
    const [disliked, setDisliked] = useState(feedback?.type === "dislike");

    const handleRetry = async () => {
      if (!conversationId || messageIndex === undefined || !onRetry) {
        console.warn(
          "Cannot retry: missing conversationId, messageIndex, or onRetry callback"
        );
        return;
      }

      try {
        await onRetry(messageIndex);
      } catch (error) {
        console.error("Failed to retry message:", error);
        toast.error("Failed to retry message");
      }
    };

    const formatProductForCopy = (
      product: SectionBasedProduct,
      isFeatured: boolean
    ): string => {
      const parts = [`Title: ${product.title}`];

      if (product.brandName) {
        parts.push(`Brand: ${product.brandName}`);
      }

      if (product.retailPrice !== undefined) {
        parts.push(`Price: $${product.retailPrice.toFixed(2)}`);
      }

      // Construct Brandclub URL
      const url = `${window.location.origin}/product/p/${product.stacklineSku}/product`;
      parts.push(`URL: ${url}`);

      // Add feature bullets for featured products
      if (
        isFeatured &&
        product.featureBullets &&
        product.featureBullets.length > 0
      ) {
        parts.push(`\nFeatures:`);
        product.featureBullets.forEach((bullet: string) => {
          parts.push(`• ${bullet}`);
        });
      }

      return parts.join("\n");
    };

    const convertProductTagsToText = (text: string): string => {
      let result = text;

      // Replace featured product tags (with or without self-closing slash)
      // Handle multiple featured products by looking them up in productMetadataMap
      result = result.replace(
        /<product\s+featured="true"\s+stacklineSku="([^"]+)"\s*(?:question="[^"]+")?\s*\/?>/g,
        (match, stacklineSku) => {
          // First try to find in productMetadataMap (for multiple featured products)
          let product = productMetadataMap.get(stacklineSku);

          // Fall back to props for backward compatibility (single featured product)
          if (!product) {
            product =
              featuredProduct?.product || streamingFeaturedProduct?.product;
            if (product && product.stacklineSku !== stacklineSku) {
              product = undefined; // Clear if SKU doesn't match
            }
          }

          if (product) {
            return "\n\n" + formatProductForCopy(product, true) + "\n\n";
          }
          return match;
        }
      );

      // Replace product group tags
      result = result.replace(
        /<product_group[^>]*>([\s\S]*?)<\/product_group>/g,
        (match, innerContent) => {
          // Extract product tags from within the group (with or without self-closing slash)
          const productTags = innerContent.match(
            /<product\s+stacklineSku="([^"]+)"\s*\/?>/g
          );
          if (!productTags) return match;

          const products: string[] = [];
          productTags.forEach((tag: string) => {
            const skuMatch = tag.match(/stacklineSku="([^"]+)"/);
            if (skuMatch) {
              const sku = skuMatch[1];
              const product = productMetadataMap.get(sku);
              if (product) {
                products.push(formatProductForCopy(product, false));
              }
            }
          });

          return products.length > 0
            ? "\n\n" + products.join("\n---\n") + "\n\n"
            : match;
        }
      );

      // Replace citation tags with source URL
      result = result.replace(
        /<citation\s+[^>]*>([\s\S]*?)<\/citation>/g,
        (match, citationText) => {
          // Extract source attribute
          const sourceMatch = match.match(/source\s*=\s*"([^"]+)"/);
          const source = sourceMatch ? sourceMatch[1] : "";

          if (source) {
            return `[${citationText.trim()}](${source})`;
          }
          return citationText;
        }
      );

      return result;
    };

    const handleCopy = async () => {
      try {
        const textWithProducts = convertProductTagsToText(content);
        await navigator.clipboard.writeText(textWithProducts);
        toast.success("Message copied to clipboard");
      } catch (error) {
        toast.error("Failed to copy message");
      }
    };

    const handleShare = async () => {
      if (!conversationId) {
        console.warn("Cannot share: missing conversationId");
        return;
      }

      try {
        // Get headers with auth and visitor ID
        const headers = await getHeadersWithUniqueVisitorId();

        // Enable sharing for the conversation
        const result = await setConversationSharing({
          conversationId,
          enabled: true,
          headersToInject: headers,
        });

        // Copy the share URL to clipboard
        await navigator.clipboard.writeText(result.shareUrl);
        toast.success("Share link copied to clipboard");
      } catch (error) {
        console.error("Failed to share conversation:", error);
        toast.error("Failed to share conversation");
      }
    };

    const handleLike = async () => {
      if (!conversationId || messageIndex === undefined) {
        toast.error("Cannot like: conversation not loaded");
        return;
      }

      try {
        const newState = !liked;

        // Get headers with auth and visitor ID
        const headers = await getHeadersWithUniqueVisitorId();

        // Call API to persist feedback FIRST
        const result = await sendMessageFeedback({
          conversationId,
          messageIndex,
          feedbackType: newState ? "like" : null,
          headersToInject: headers,
        });

        // Only update UI state after successful API call
        setLiked(newState);
        setDisliked(false);
        toast.success(newState ? "Message liked" : "Like removed");

        // Update parent component state with new feedback
        if (onUpdateFeedback && result.message.metadata?.feedback) {
          onUpdateFeedback(messageIndex, result.message.metadata.feedback);
        }
      } catch (error) {
        console.error("Failed to update feedback:", error);
        toast.error("Failed to save feedback");
      }
    };

    const handleDislike = async () => {
      if (!conversationId || messageIndex === undefined) {
        toast.error("Cannot dislike: conversation not loaded");
        return;
      }

      try {
        const newState = !disliked;

        // Get headers with auth and visitor ID
        const headers = await getHeadersWithUniqueVisitorId();

        // Call API to persist feedback FIRST
        const result = await sendMessageFeedback({
          conversationId,
          messageIndex,
          feedbackType: newState ? "dislike" : null,
          headersToInject: headers,
        });

        // Only update UI state after successful API call
        setDisliked(newState);
        setLiked(false);
        toast.success(newState ? "Message disliked" : "Dislike removed");

        // Update parent component state with new feedback
        if (onUpdateFeedback && result.message.metadata?.feedback) {
          onUpdateFeedback(messageIndex, result.message.metadata.feedback);
        }
      } catch (error) {
        console.error("Failed to update feedback:", error);
        toast.error("Failed to save feedback");
      }
    };

    return (
      <div
        className={`message ${role}`}
        style={{
          display: "flex",
          justifyContent: isUser ? "flex-end" : "flex-start",
          flexDirection: "column",
          alignItems: isUser ? "flex-end" : "flex-start",
        }}
      >
        {/* Preview components for assistant messages / chain of thought */}
        {!isUser &&
          (previewSearchQueries.length > 0 ||
            previewQueryProducts.length > 0) && (
            <ChainOfThought
              previewSearchQueries={previewSearchQueries}
              previewQueryProducts={previewQueryProducts}
              isStreaming={isStreaming}
              currentStatus={currentStatus}
              hasContent={content.length > 0}
              isLastMessage={isLastMessage}
            />
          )}

        {/* Show "Thinking" shimmer when typing */}
        {!isUser && isTyping && (
          <div style={{ maxWidth: "100%" }}>
            <div
              className="message-content"
              style={{
                maxWidth: "100%",
                backgroundColor: "#fff",
                color: theme.palette.text.primary,
                borderRadius: "18px",
                borderBottomLeftRadius: "5px",
                borderBottomRightRadius: "18px",
                wordWrap: "break-word",
                padding: "0.75rem 1rem",
              }}
            >
              <Shimmer as="p" className="tw:text-sm" duration={1.5}>
                Thinking...
              </Shimmer>
            </div>
          </div>
        )}

        {/* User message image - render ABOVE the bubble */}
        {isUser && (imageUrl || imageData) && (
          <div
            style={{
              maxWidth: "70%",
              marginBottom: "0.5rem",
            }}
          >
            <img
              src={imageUrl || `data:${imageMimeType};base64,${imageData}`}
              alt="User uploaded image"
              style={{
                width: "100%",
                maxHeight: "400px",
                objectFit: "cover",
                display: "block",
                borderRadius: "12px",
              }}
            />
          </div>
        )}

        {/* Message content */}
        {!isTyping ? (
          <div style={{ maxWidth: isUser ? "70%" : "100%" }}>
            <div
              className="message-content"
              style={{
                maxWidth: "100%",
                backgroundColor: isUser ? theme.palette.primary.main : "#fff",
                color: isUser ? "#fff" : theme.palette.text.primary,
                borderRadius: "18px",
                borderBottomLeftRadius: isUser ? "18px" : "5px",
                borderBottomRightRadius: isUser ? "5px" : "18px",
                wordWrap: "break-word",
                padding: isUser ? "0.75rem 1rem" : "0",
              }}
            >
              {isUser ? (
                <div style={{ whiteSpace: "pre-wrap" }}>{content}</div>
              ) : (
                <>
                  {renderMessageContentWithCarousels(
                    content,
                    productMetadataMap,
                    streamingProductGroups,
                    isStreaming,
                    isStreaming // streaming messages are already processed
                  )}
                  {/* Show cursor for streaming messages only when we have content */}
                  {isStreaming && content.length > 0 && (
                    <span
                      style={{
                        display: "inline-block",
                        width: "2px",
                        height: "1em",
                        backgroundColor: "currentColor",
                        animation: "blink 1s infinite",
                        marginLeft: "2px",
                      }}
                    />
                  )}
                </>
              )}
            </div>

            {bottomRef && (
              <div
                style={{
                  height: "1px", // Back to 1px since we're using rootMargin for detection
                  width: "100%",
                }}
                ref={bottomRef}
              />
            )}

            {/* Action buttons for assistant messages */}
            {!isUser && !isTyping && !isStreaming && content.length > 0 ? (
              <Actions className="tw:mt-2">
                {/* <Action label="Retry" tooltip="Retry" onClick={handleRetry}>
                  <RefreshCcwIcon className="tw:size-4" />
                </Action> */}
                <Action label="Copy" tooltip="Copy" onClick={handleCopy}>
                  <CopyIcon className="tw:size-4" />
                </Action>
                <Action label="Like" tooltip="Like" onClick={handleLike}>
                  <ThumbsUpIcon
                    className={`tw:size-4 ${
                      liked ? "tw:fill-current tw:text-primary" : ""
                    }`}
                  />
                </Action>
                <Action
                  label="Dislike"
                  tooltip="Dislike"
                  onClick={handleDislike}
                >
                  <ThumbsDownIcon
                    className={`tw:size-4 ${
                      disliked ? "tw:fill-current tw:text-primary" : ""
                    }`}
                  />
                </Action>

                <Action label="Share" tooltip="Share" onClick={handleShare}>
                  <ShareIcon className="tw:size-4" />
                </Action>
              </Actions>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }
);

// Memoize the component to prevent unnecessary re-renders
export const ChatMessage = React.memo(
  ChatMessageComponent,
  (prevProps, nextProps) => {
    // Custom comparison function to prevent re-renders when props haven't meaningfully changed
    return (
      prevProps.role === nextProps.role &&
      prevProps.content === nextProps.content &&
      prevProps.isTyping === nextProps.isTyping &&
      prevProps.bottomRef === nextProps.bottomRef &&
      prevProps.isStreaming === nextProps.isStreaming &&
      prevProps.isLastMessage === nextProps.isLastMessage &&
      prevProps.currentStatus === nextProps.currentStatus &&
      prevProps.isWaitingForFollowUpQueries ===
        nextProps.isWaitingForFollowUpQueries &&
      prevProps.previewSearchQueries?.length ===
        nextProps.previewSearchQueries?.length &&
      prevProps.previewQueryProducts?.length ===
        nextProps.previewQueryProducts?.length &&
      prevProps.followUpQueries?.length === nextProps.followUpQueries?.length &&
      prevProps.productMetadataMap === nextProps.productMetadataMap &&
      prevProps.streamingProductGroups === nextProps.streamingProductGroups &&
      prevProps.featuredProduct === nextProps.featuredProduct &&
      prevProps.streamingFeaturedProduct ===
        nextProps.streamingFeaturedProduct &&
      prevProps.imageUrl === nextProps.imageUrl &&
      prevProps.imageData === nextProps.imageData &&
      prevProps.imageMimeType === nextProps.imageMimeType
    );
  }
);
